import { getAuth, onAuthStateChanged } from 'firebase/auth';

/**
 * Firebase ID-token access for the data layer.
 *
 * Every thunk used to open its own `onAuthStateChanged` subscription and await
 * `getIdToken()` before it could issue a request - 209 of 227 of them, via 11
 * copy-pasted wrapper factories and 78 hand-inlined duplicates. Besides the
 * per-request listener churn, wrapping each thunk body in `new Promise(...)`
 * made `api.getState` and `api.signal` structurally unreachable, which is why
 * the codebase had zero uses of either.
 *
 * This module registers ONE listener for the lifetime of the app. Callers get
 * the token from `getAuthToken()`, and the axios request interceptor attaches
 * it, so thunks can be plain async functions again.
 */

/** Resolves the first time Firebase reports a definite auth state. */
let readyPromise = null;

/**
 * Waits for Firebase to settle its initial auth state.
 *
 * Firebase restores the persisted session asynchronously, so a request issued
 * during startup must wait or it would go out unauthenticated. Only the *first*
 * resolution is awaited; after that `auth.currentUser` is read live, so a later
 * sign-in or sign-out is always reflected.
 *
 * @returns {Promise<void>} resolves once the initial state is known
 */
const waitForAuthReady = () => {
  if (!readyPromise) {
    readyPromise = new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(
        getAuth(),
        () => {
          unsubscribe();
          resolve();
        },
        () => {
          unsubscribe();
          resolve();
        },
      );
    });
  }
  return readyPromise;
};

/**
 * Returns the current user's Firebase ID token, or null when signed out.
 *
 * Firebase caches the token internally and refreshes it about five minutes
 * before expiry, so calling this per request costs nothing on the network.
 *
 * @returns {Promise<string|null>} the ID token, or null if not signed in
 */
export const getAuthToken = async () => {
  await waitForAuthReady();
  const user = getAuth().currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    // A failed refresh is reported by the caller's request as a 401 rather than
    // being swallowed into a generic "not authenticated" here.
    return null;
  }
};

/**
 * Returns the signed-in Firebase user, or null.
 *
 * @returns {Promise<import('firebase/auth').User|null>} the current user
 */
export const getAuthUser = async () => {
  await waitForAuthReady();
  return getAuth().currentUser;
};

/**
 * Backwards-compatible alias for the original helper.
 *
 * @returns {Promise<string|null>} the ID token, or null if not signed in
 */
export const getUserToken = getAuthToken;

export default getAuthToken;
